import { useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Select, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Check, Plus, X } from 'lucide-react';
import Layout from '../../Layout';
import { ktApi, useKt } from './ktStore';
import { caseBlock, CASE_STATUS, refundQuoteOf } from './ktCards';
import { fmtMoney, needsRef, PAY_METHOD, REF_LABEL, TIER, type PayMethod, type RefundCase } from './ktLogic';
import { DemoBar, PageHeader, Panel, showErrors } from './ktUi';

const STATUS_COLOR: Record<RefundCase['status'], string> = { pending_l1: 'orange', pending_l2: 'gold', approved: 'blue', paid: 'green', rejected: 'default' };
const METHODS: PayMethod[] = ['transfer', 'cash', 'vietqr'];
const money = (fmt: number | string | undefined) => `${fmt}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const unmoney = (v: string | undefined) => Number((v || '').replace(/\./g, ''));

/** Hủy thẻ, hoàn tiền: duyệt 2 cấp, tự trừ phạt, xét nghiệm, phí cà thẻ, truy thu hoa hồng (SRS 2.4) */
export default function KeToanRefunds() {
  const s = useKt();
  const [creating, setCreating] = useState(false);
  const [rejecting, setRejecting] = useState<RefundCase | null>(null);
  const [paying, setPaying] = useState<RefundCase | null>(null);
  const [note, setNote] = useState('');
  const [method, setMethod] = useState<PayMethod>('transfer');
  const [ref, setRef] = useState('');
  const list = useMemo(() => [...s.refundCases].sort((a, b) => b.at - a.at), [s.refundCases]);
  const cardOf = (id: string) => s.cards.find(c => c.id === id);

  const canApprove = (r: RefundCase) => (r.status === 'pending_l1' && s.tier === 'revenue') || (r.status === 'pending_l2' && s.tier === 'chief');
  const columns: ColumnsType<RefundCase> = [
    { title: 'Hồ sơ', key: 'n', render: (_, r) => (
      <div className="min-w-0">
        <div className="font-medium text-gray-900 dark:text-gray-100">{r.no} · {cardOf(r.cardId)?.customerName}</div>
        <div className="text-[11px] text-gray-500">{cardOf(r.cardId)?.packageName}</div>
        <div className="text-[11px] text-gray-500">{r.reason}</div>
      </div>
    ) },
    { title: 'Đã đóng', key: 'p', width: 120, align: 'right', render: (_, r) => fmtMoney(r.paid) },
    { title: 'Đã dùng', key: 'c', width: 120, align: 'right', render: (_, r) => fmtMoney(r.consumed) },
    { title: `Phạt hủy`, key: 'f', width: 120, align: 'right', render: (_, r) => <div>{fmtMoney(r.penalty)}<div className="text-[11px] text-gray-500">{r.penaltyPct}%</div></div> },
    { title: 'Xét nghiệm', key: 't', width: 110, align: 'right', render: (_, r) => fmtMoney(r.testFee) },
    { title: 'Phí cà thẻ', key: 'pf', width: 110, align: 'right', render: (_, r) => fmtMoney(r.posFee) },
    { title: 'Hoàn nét', key: 'net', width: 130, align: 'right', render: (_, r) => <b>{fmtMoney(r.net)}</b> },
    { title: 'Trạng thái', key: 's', width: 170, render: (_, r) => <div><Tag color={STATUS_COLOR[r.status]} className="!mr-0">{CASE_STATUS[r.status]}</Tag>{r.ref && <div className="text-[11px] text-gray-500 mt-0.5">{r.ref}</div>}</div> },
    { title: '', key: 'a', width: 190, align: 'right', render: (_, r) => (
      <div className="flex justify-end gap-1">
        {canApprove(r) && <Button size="small" type="primary" icon={<Check size={13} />} onClick={() => { const x = ktApi.approveCase(r.id); x.ok ? message.success('Đã duyệt') : showErrors(x.errors); }}>Duyệt</Button>}
        {canApprove(r) && <Button size="small" danger icon={<X size={13} />} onClick={() => { setNote(''); setRejecting(r); }}>Từ chối</Button>}
        {r.status === 'approved' && s.tier === 'revenue' && <Button size="small" type="primary" onClick={() => { setMethod('transfer'); setRef(''); setPaying(r); }}>Đã chi</Button>}
      </div>
    ) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Duyệt hủy thẻ, hoàn tiền" desc="Quầy lập hồ sơ, Kế toán nội bộ duyệt cấp 1, Kế toán trưởng duyệt cấp 2. Hoàn nét = đã đóng − đã dùng − phạt − xét nghiệm − phí cà thẻ"
          extra={s.tier !== 'chief' ? <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>Lập hồ sơ</Button> : undefined} />
        <DemoBar />
        <Panel title={`Hồ sơ (${list.length})`}>
          <Table rowKey="id" size="middle" columns={columns} dataSource={list} pagination={false} scroll={{ x: 1250 }} locale={{ emptyText: 'Chưa có hồ sơ' }}
            expandable={{ expandedRowRender: r => (
              <div className="text-xs space-y-1">
                {r.steps.map(x => <div key={`${x.at}${x.action}`}><b>{x.action}</b> · {TIER[x.tier].short} · {dayjs(x.at).format('HH:mm DD/MM/YYYY')}{x.note ? ` · ${x.note}` : ''}</div>)}
                {r.status === 'paid' && <div className="text-gray-500">Đã tự sinh bút toán hoàn thẻ, báo điều chỉnh hóa đơn và truy thu hoa hồng ở kỳ chi tiền.</div>}
              </div>
            ) }} />
        </Panel>
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} />}

      <Modal open={!!rejecting} title={`Từ chối ${rejecting?.no}`} okText="Từ chối" okButtonProps={{ danger: true }} cancelText="Quay lại" onCancel={() => setRejecting(null)} destroyOnHidden
        onOk={() => { const r = ktApi.rejectCase(rejecting!.id, note); if (r.ok) { message.success('Đã từ chối'); setRejecting(null); } else showErrors(r.errors); }}>
        <label htmlFor="kt-case-note" className="text-sm font-medium">Lý do</label>
        <Input.TextArea id="kt-case-note" rows={3} value={note} onChange={e => setNote(e.target.value)} className="!mt-1" />
      </Modal>

      <Modal open={!!paying} title={`Xác nhận đã chi ${paying ? fmtMoney(paying.net) : ''}`} okText="Xác nhận" cancelText="Quay lại" onCancel={() => setPaying(null)} destroyOnHidden
        onOk={() => { const r = ktApi.payCase(paying!.id, method, ref); if (r.ok) { message.success('Đã ghi nhận, bút toán và truy thu hoa hồng tự sinh'); setPaying(null); } else showErrors(r.errors); }}>
        <div className="space-y-3">
          <div><label htmlFor="kt-case-method" className="text-sm font-medium">Hình thức</label><Select id="kt-case-method" className="w-full" value={method} onChange={setMethod} options={METHODS.map(m => ({ value: m, label: PAY_METHOD[m] }))} /></div>
          <div><label htmlFor="kt-case-ref" className="text-sm font-medium">{REF_LABEL[method]}</label><Input id="kt-case-ref" disabled={!needsRef(method)} value={ref} onChange={e => setRef(e.target.value)} /></div>
        </div>
      </Modal>
    </Layout>
  );
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const s = useKt();
  const options = s.cards.filter(c => !caseBlock(s, c)).map(c => ({ value: c.id, label: `${c.customerName} · ${c.packageName}` }));
  const [cardId, setCardId] = useState(options[0]?.value || '');
  const card = s.cards.find(c => c.id === cardId);
  const [penaltyPct, setPenaltyPct] = useState(card?.policy?.refundFeePct ?? 10);
  const [testFee, setTestFee] = useState(0);
  const [reason, setReason] = useState('');
  const q = card ? refundQuoteOf(s, card, { penaltyPct, testFee }) : null;
  const rows = q ? [['Đã đóng', q.paid], ['Giá trị đã dùng', -q.consumed], ['Phạt hủy thẻ', -q.penalty], ['Phí xét nghiệm', -q.testFee], ['Phí cà thẻ POS', -q.posFee]] as const : [];
  return (
    <Modal open title="Lập hồ sơ hủy thẻ, hoàn tiền" okText="Gửi duyệt" cancelText="Thôi" onCancel={onClose} destroyOnHidden okButtonProps={{ disabled: !card }}
      onOk={() => { const r = ktApi.createRefundCase(cardId, { penaltyPct, testFee, reason }); if (r.ok) { message.success('Đã gửi Kế toán nội bộ duyệt'); onClose(); } else showErrors(r.errors); }}>
      <div className="space-y-3">
        {!options.length && <Alert type="info" showIcon message="Không có thẻ nào đủ điều kiện hoàn: thẻ phải đã trả đủ và còn phần chưa dùng" />}
        <div><label htmlFor="kt-case-card" className="text-sm font-medium">Thẻ</label><Select id="kt-case-card" className="w-full" showSearch optionFilterProp="label" value={cardId || undefined} onChange={setCardId} options={options} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="kt-case-pct" className="text-sm font-medium">Phạt hủy (%)</label><InputNumber id="kt-case-pct" className="!w-full" min={0} max={100} value={penaltyPct} onChange={v => setPenaltyPct(Number(v) || 0)} /></div>
          <div><label htmlFor="kt-case-test" className="text-sm font-medium">Phí xét nghiệm</label><InputNumber id="kt-case-test" className="!w-full" min={0} step={100000} value={testFee} onChange={v => setTestFee(Number(v) || 0)} formatter={money} parser={unmoney} /></div>
        </div>
        <div><label htmlFor="kt-case-reason" className="text-sm font-medium">Lý do</label><Input.TextArea id="kt-case-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} /></div>
        {q && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm space-y-0.5">
            {rows.map(([label, v]) => <div key={label} className="flex justify-between"><span className="text-gray-500">{label}</span><span>{fmtMoney(v)}</span></div>)}
            <div className="flex justify-between font-semibold border-t border-gray-200 dark:border-gray-700 pt-1 mt-1"><span>Hoàn nét</span><span>{fmtMoney(q.net)}</span></div>
          </div>
        )}
      </div>
    </Modal>
  );
}
